// Client-side view of a decoration-slot PROPOSAL.
//
// `requestedit` — the form behind a proof email's "Request changes" link — can
// only RECORD a decoration change, never apply one: its caller holds the
// customer's proof-link token, not a worker's session, so nothing it submits may
// reach the live decoration config that preflight, the proof and the SKU read.
// Applying one is a worker action, and this is its front end.
//
// Everything here is pure. The server (ghostfit-console/lib/slot-proposal.js) is
// the authority on the plain-language summaries and on whether a proposal is
// still promotable — it re-decides both inside the promotion transaction. What
// this module owns is presentation: what the panel says, what the confirm step
// asks, and why the Promote button is disabled when it is.

export type ProposalChangeKind = 'artwork' | 'added' | 'removed' | 'kind';
export type ProposalDecorationKind = 'logo' | 'name' | 'number';

export type ProposalChange = {
  slotId: string | null;
  code: string | null;
  kind: ProposalDecorationKind | null;
  changed: ProposalChangeKind | string | null;
  /** Registry label, or the raw code when the registry could not be read. */
  label: string;
  view: string | null;
  known: boolean;
  recognised: boolean;
  /** The server's sentence — "Add Left Sleeve (logo)". */
  summary: string;
};

export type ProposalArtwork = {
  slotId: string | null;
  fileField: string | null;
  originalFilename: string | null;
  sha256: string | null;
  /** Server-resolved image route. The GCS path never reaches the browser. */
  imageUrl: string | null;
};

export type SlotProposal = {
  revisionIndex: number;
  revisionNumber: number | null;
  /** 1..MAX_PRIZE_TIERS (8). T3+ live on `tiers[]`. */
  tier: number;
  status: string | null;
  schema: number | null;
  schemaSupported: boolean;
  requestedAt: string | null;
  /** The customer's own words from the request form. */
  notes: string | null;
  source: string | null;
  productId: string | null;
  changes: ProposalChange[];
  artwork: ProposalArtwork[];
  stale: boolean;
  staleReasons: string[];
  promotedAt: string | null;
  promotedBy: string | null;
  promotable: boolean;
  promoteUrl: string;
};

export type ProposalsPayload = {
  submissionId: string;
  /** Set when the decoration registry was unreachable — labels are raw codes. */
  registryError: string | null;
  proposals: SlotProposal[];
};

export const PENDING_PROMOTION = 'pending_promotion';

/** Stable identity for React keys and per-row busy state. */
export function proposalKey(p: Pick<SlotProposal, 'revisionIndex' | 'tier'>): string {
  return `${p.revisionIndex}:${p.tier}`;
}

/** The proposals a worker can still act on, oldest request first. */
export function pendingProposals(payload: ProposalsPayload | undefined): SlotProposal[] {
  if (!payload || !Array.isArray(payload.proposals)) return [];
  return payload.proposals.filter((p) => p.status === PENDING_PROMOTION);
}

export function proposalsForTier(payload: ProposalsPayload | undefined, tier: number): SlotProposal[] {
  return pendingProposals(payload).filter((p) => p.tier === tier);
}

/** "2 changes · tier 1" — the panel's one-line heading. */
export function proposalHeadline(p: SlotProposal): string {
  const n = p.changes.length;
  return `${n} requested change${n === 1 ? '' : 's'} · tier ${p.tier}`;
}

/**
 * Why Promote is unavailable, in the words the worker needs — or null when it
 * is available. The server decides this too (and again inside the transaction);
 * this exists so the button explains itself instead of just being grey.
 */
export function blockReason(p: SlotProposal): string | null {
  if (p.status !== PENDING_PROMOTION) {
    if (p.status === 'promoted') {
      return `Already applied${p.promotedBy ? ` by ${p.promotedBy}` : ''}. Nothing left to do.`;
    }
    return `This request is “${p.status ?? 'unknown'}”, not pending — it cannot be applied.`;
  }
  if (!p.schemaSupported) {
    return `This request was recorded in a newer format (schema ${p.schema ?? '?'}) than this console understands. Deploy the current console before applying it.`;
  }
  if (p.stale) {
    return `Tier ${p.tier} changed after this was requested, so these changes were worked out against a setup that no longer exists: ${p.staleReasons.join('; ')}. Ask the customer to re-request from their latest proof email, or make the change yourself on the tier's decoration cards.`;
  }
  return null;
}

/** The confirm() text. Names every change — this rewrites what will be printed. */
export function confirmMessage(p: SlotProposal): string {
  const lines = p.changes.map((c) => `  • ${c.summary}`).join('\n');
  return [
    `Apply ${p.changes.length} change${p.changes.length === 1 ? '' : 's'} to tier ${p.tier}?`,
    '',
    lines,
    '',
    `Tier ${p.tier}'s preflight will be cleared, so you must run preflight again before this can be approved.`,
  ].join('\n');
}

/** What a worker is told the moment a promotion lands. */
export function promotedMessage(p: SlotProposal): string {
  return `Tier ${p.tier} print locations updated — run preflight for tier ${p.tier} before approving.`;
}

/** Colour family per change verb, for the badge on each row. */
export function changeTone(changed: ProposalChange['changed']): 'add' | 'remove' | 'artwork' | 'kind' | 'unknown' {
  switch (changed) {
    case 'added': return 'add';
    case 'removed': return 'remove';
    case 'artwork': return 'artwork';
    case 'kind': return 'kind';
    default: return 'unknown';
  }
}

/** Short verb for the badge. */
export function changeVerb(changed: ProposalChange['changed']): string {
  switch (changed) {
    case 'added': return 'add';
    case 'removed': return 'remove';
    case 'artwork': return 'new art';
    case 'kind': return 'kind';
    default: return String(changed ?? 'unknown');
  }
}

/** The uploaded artwork for a change row, when the request carried any. */
export function artworkFor(p: SlotProposal, slotId: string | null): ProposalArtwork | null {
  if (!slotId) return null;
  return p.artwork.find((a) => a.slotId === slotId) || null;
}

/**
 * Every artwork the request carried, in change order — a change that named no
 * slot still shows its file, because a customer who uploaded something expects
 * a human to look at it.
 */
export function artworkGallery(p: SlotProposal): ProposalArtwork[] {
  return p.artwork.filter((a) => !!a.imageUrl);
}
